import React, { ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import moment from 'moment-timezone';

import { Colors, Theme } from '@app/theme';
import { ActionCausedByRole, HistoryEvent } from '@app/api';
import classnames from 'classnames';
import { Chip } from '@app/core';
import { useTranslation } from 'react-i18next';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { EventCausedByCell } from './EventCausedByCell';

const useStyles = createUseStyles((theme: Theme) => ({
  cell: {
    verticalAlign: 'top',
  },
  chip: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: theme.height.chips,
    padding: 0,
  },
  icon: {
    fontSize: theme.iconSize.small,
    fontWeight: 500,
  },
  cellContent: {
    minHeight: theme.height.chips,
    display: 'flex',
    alignItems: 'center',
  },

  separator: {
    width: 4,
    height: 4,
    borderRadius: 2,
    background: theme.colors.inkTertiary,
    position: 'relative',
    top: 2,
  },
  caption: {
    ...theme.fonts.caption,
  },
  rightChildren: {
    verticalAlign: 'top',
  },
  rightChildrenContent: {
    position: 'relative',
    top: 3,
  },
  downContent: {
    paddingTop: theme.marginBase / 2,
    paddingLeft: theme.marginBase * 4,
  },
  eventName: {
    ...theme.fonts.label,
  },
  emptyLine: {
    height: theme.marginBase,
    width: 1,
  },
  detailsContainer: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
  },
  detailsContainerWhite: {
    backgroundColor: theme.colors.white,
  },
  details: {
    whiteSpace: 'pre-wrap',
    ...theme.fonts.caption,
  },
}));

interface Props {
  event: HistoryEvent;
  icon?: string;
  rightChildren?: ReactNode;
  details?: ReactNode | string;
  context?: string;
  overGray?: boolean;
  anonymousCausedBy?: ActionCausedByRole;
}

const categoryDefaultIcons: Record<string, string> = {
  session: 'ri-edit-line',
  application: 'ri-list-check-2',
  recruitment: 'ri-play-line',
  invitation: 'ri-send-plane-2-line',
  task: 'ri-calendar-event-line',
  order: 'ri-coins-line',
  incentive_payment: 'ri-heart-line',
  email: 'ri-mail-line',
  message: 'ri-mail-line',
  participation: 'ri-information-line',
};

const colorsByRole: Record<ActionCausedByRole, Colors> = {
  [ActionCausedByRole.AUTOMATIC]: 'red',
  [ActionCausedByRole.ADMIN]: 'purple',
  [ActionCausedByRole.CLIENT]: 'green',
  [ActionCausedByRole.PARTICIPANT]: 'blue',
  [ActionCausedByRole.ANONYMOUS_USER]: 'ink',
};

export const EventHistoryRow = ({
  event,
  icon,
  rightChildren,
  details,
  context,
  overGray,
  anonymousCausedBy,
}: Props) => {
  const classes = useStyles();
  const { i18n, t } = useTranslation();

  const date = moment(event.date).toDate();

  const color = colorsByRole[event.actionCausedBy.role];

  return (
    <>
      <tr>
        <td className={classes.cell}>
          <Chip color={color} className={classes.chip}>
            <i
              className={classnames(
                icon ||
                  categoryDefaultIcons[event.category] ||
                  'ri-calendar-event-line',
                classes.icon,
              )}
            />
          </Chip>
        </td>
        <td className={classes.cell}>
          <div className={classnames(classes.cellContent, classes.caption)}>
            {Intl.DateTimeFormat('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            }).format(date)}
          </div>
        </td>
        <td className={classes.cell}>
          <div className={classes.cellContent}>
            <div className={classes.separator} />
          </div>
        </td>
        <td className={classes.cell}>
          <div className={classnames(classes.cellContent, classes.caption)}>
            {Intl.DateTimeFormat('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
            }).format(date)}
          </div>
        </td>
        <td className={classes.cell}>
          <div className={classes.cellContent}>
            <div className={classes.separator} />
          </div>
        </td>
        <td className={classes.cell}>
          <div className={classes.cellContent}>
            <EventCausedByCell
              causedBy={event.actionCausedBy}
              color={color}
              anonymousCausedBy={anonymousCausedBy}
            />
          </div>
        </td>
        <td className={classes.cell}>
          <div className={classes.cellContent}>
            <div className={classes.separator} />
          </div>
        </td>

        <td className={classes.rightChildren}>
          <div className={classes.rightChildrenContent}>
            {rightChildren ? (
              rightChildren
            ) : (
              <div className={classes.eventName}>
                {i18n.exists(`eventHistory.events.${event.eventName}`)
                  ? t(`eventHistory.events.${event.eventName}`, { context })
                  : upperFirst(lowerCase(event.eventName))}
              </div>
            )}
          </div>
        </td>
      </tr>
      {!!details && (
        <tr className={classes.downContent}>
          <td />
          <td colSpan={7}>
            <div
              className={classnames(classes.detailsContainer, {
                [classes.detailsContainerWhite]: overGray,
              })}
            >
              <p className={classes.details}>{details}</p>
            </div>
          </td>
        </tr>
      )}
      <tr>
        <td colSpan={8}>
          <div className={classes.emptyLine} />
        </td>
      </tr>
    </>
  );
};
